import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Mic, MessageSquare, Sparkles, X } from 'lucide-react'
import { getConciergeConfig, type ConciergeMode } from '../../config/concierge'
import { voice, type BookingIntent, type ConversationHandle } from '../../lib/voice'

interface ConciergeWidgetProps {
  /** Override the global mode for a single page mount. */
  modeOverride?: ConciergeMode
}

function iconFor(mode: ConciergeMode): ReactElement {
  if (mode === 'voice_only') return <Mic size={20} />
  if (mode === 'chat_only') return <MessageSquare size={20} />
  return <Sparkles size={20} />
}

function labelKey(mode: ConciergeMode): string {
  if (mode === 'voice_only') return 'concierge.voiceLabel'
  if (mode === 'chat_only') return 'concierge.chatLabel'
  return 'concierge.multimodalLabel'
}

export default function ConciergeWidget({ modeOverride }: ConciergeWidgetProps) {
  const config = useMemo(() => getConciergeConfig(), [])
  const mode = modeOverride ?? config.mode
  const { t } = useTranslation()
  const { lang = 'en' } = useParams<{ lang: string }>()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const handleRef = useRef<ConversationHandle | null>(null)

  // Tear down any active session when the widget unmounts.
  useEffect(() => {
    return () => {
      handleRef.current?.end().catch(() => {/* ignore on unmount */})
      handleRef.current = null
    }
  }, [])

  if (!config.enabled || !config.agentId) return null

  function handleIntent(intent: BookingIntent): void {
    if (intent.kind === 'open_booking') {
      navigate(`/${lang}/branches?service=${encodeURIComponent(intent.serviceSlug)}`)
      setOpen(false)
      return
    }
    if (intent.kind === 'contact_human') {
      navigate(`/${lang}/contact`)
      setOpen(false)
      return
    }
    // 'live_pricing' is consumed by the agent itself; nothing to navigate.
  }

  async function handleOpen(): Promise<void> {
    if (handleRef.current) {
      setOpen(true)
      return
    }
    setError(null)
    setOpen(true)
    try {
      const handle = await voice.startConversation({
        agentId: config.agentId,
        mode,
        signedUrlEndpoint: config.signedUrlEndpoint,
        onIntent: handleIntent,
      })
      handleRef.current = handle
    } catch {
      setError(t('concierge.error.start'))
    }
  }

  async function handleClose(): Promise<void> {
    setOpen(false)
    if (handleRef.current) {
      try {
        await handleRef.current.end()
      } catch {
        /* ignore — session may already be terminated */
      }
      handleRef.current = null
    }
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={handleOpen}
          aria-label={t(labelKey(mode))}
          className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-gold text-ivory shadow-lg hover:shadow-gold-glow transition-shadow flex items-center justify-center"
          data-testid="concierge-launcher"
          data-mode={mode}
        >
          {iconFor(mode)}
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label={t(labelKey(mode))}
          className="fixed bottom-5 right-5 z-50 w-[min(360px,calc(100vw-2.5rem))] bg-ivory border border-mocha/15 rounded-lg shadow-xl overflow-hidden"
          data-testid="concierge-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-mocha/10 bg-ivory-dark">
            <div className="flex items-center gap-2 font-display text-mocha">
              {iconFor(mode)}
              <span className="text-sm">{t(labelKey(mode))}</span>
            </div>
            <button
              type="button"
              onClick={handleClose}
              aria-label={t('concierge.aria.close')}
              className="text-mocha/60 hover:text-mocha transition-colors p-1"
            >
              <X size={18} />
            </button>
          </div>

          <div className="p-4 min-h-[180px] flex items-center justify-center">
            {error ? (
              <p role="alert" className="font-body text-sm text-mocha/70 text-center">
                {error}
              </p>
            ) : (
              <p className="font-body text-sm text-mocha/55 text-center">
                {t('concierge.disclosure')}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  )
}
